/**
 * Where /app lands, and which page a sign-in or an org switch keeps (the Navigation review, D2 and D11, approved by
 * Arjun on 2026-10-06).
 *
 * The landing waits until it knows the org, its packs and, with the foundry pack, whether a brief is served. Then it
 * goes to Today. An org whose first analysis has not run yet, a brief read that fails, or one that has not answered in
 * LANDING_WAIT_MS, goes to Dashboards. A vendor's admins land on Fleet, and its other members on Settings, the one item
 * their menu holds (the Fleet page sends anyone else back to /app, which used to send them to Fleet again: a loop).
 *
 * Sign-in returns a person to the page they had open, in the same org. An org switch keeps only a page that names
 * nothing of the old org and that the new org's menu shows: a menu page, with its tab. Any page with an id (a dashboard,
 * a report, a study, a digest, a project, a station, a lot) lands on the new org's landing page instead. The address
 * rule and the storage are return-to.ts's.
 */
import {
  DASHBOARDS_PATH,
  FLEET_PATH,
  MENU_PATHS,
  SETTINGS_PATH,
  TODAY_PATH,
  menuHolds,
  offMenuHolds,
  type MenuArea,
  type MenuContext,
} from "./menu";
import { rememberKeptPage, safeAppPath } from "./return-to";

/** How long the landing waits for the brief's read before it goes to Dashboards. */
export const LANDING_WAIT_MS = 5000;

/** What the landing knows of the brief: served, none written yet or not served, the read failed, or still asked. */
export type BriefState = "served" | "none" | "failed" | "pending";

export interface LandingContext {
  vendorOrg: boolean;
  vendorAdmin: boolean;
  /** The org's packs are known (the read answered). */
  packsKnown: boolean;
  foundry: boolean;
  brief: BriefState;
  /** The landing has waited LANDING_WAIT_MS. */
  timedOut: boolean;
}

/** Where /app lands; null while it should wait. */
export function landingOf(c: LandingContext): string | null {
  if (c.vendorOrg) return c.vendorAdmin ? FLEET_PATH : SETTINGS_PATH;
  if (!c.packsKnown) return c.timedOut ? DASHBOARDS_PATH : null;
  if (!c.foundry) return DASHBOARDS_PATH;
  switch (c.brief) {
    case "served":
      return TODAY_PATH;
    case "pending":
      return c.timedOut ? DASHBOARDS_PATH : null;
    default:
      return DASHBOARDS_PATH;
  }
}

/**
 * The page an org switch keeps, from the page open in the old org: a menu page (never one with an id), with its tab
 * (the `tab` query, alone). null when the page is no menu page.
 */
export function keptPageOf(pathname: string, search: string): string | null {
  const path = pathname.length > 5 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (!MENU_PATHS.includes(path)) return null;
  const tab = new URLSearchParams(search).get("tab");
  const kept = tab && /^[\w-]{1,64}$/.test(tab) ? `${path}?tab=${tab}` : path;
  return safeAppPath(kept);
}

/**
 * Whether the new org shows a kept page (its path, without the tab): its menu holds it, or, given the viewer's menu
 * context, it is a page with no item that the viewer may open (the consultant's: menu.ts OFF_MENU).
 */
export function keptPageShown(areas: MenuArea[], kept: string, ctx?: MenuContext): boolean {
  const path = kept.split("?")[0];
  return menuHolds(areas, path) || (ctx !== undefined && offMenuHolds(ctx, path));
}

/** Keeps the page open in the old org, if an org switch may keep it (keptPageOf), for the new org's landing. */
export function keepPageOnSwitch(pathname: string, search: string): void {
  rememberKeptPage(keptPageOf(pathname, search));
}
